// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useLang from "@/hooks/useLang";
import DashboardLayout from "@/UI/components/DashboardLayout";
import PopupPaper from "@/UI/components/PopupPaper";
import RoleRoute from "@/UI/components/RoleRoute";
import AdminsScreen from "@/UI/screens/AdminsScreen";
import ClientScreen from "@/UI/screens/ClientScreen";
import ClientsScreen from "@/UI/screens/ClientsScreen";
import EnquiriesScreen from "@/UI/screens/EnquiriesScreen";
import HomeScreen from "@/UI/screens/HomeScreen";
import LoginScreen from "@/UI/screens/LoginScreen";
import NotFoundScreen from "@/UI/screens/NotFoundScreen";
import OrganizationScreen from "@/UI/screens/OrganizationScreen";
import OrganizationsScreen from "@/UI/screens/OrganizationsScreen";
import ProfileScreen from "@/UI/screens/ProfileScreen";
import SubscriptionsScreen from "@/UI/screens/SubscriptionsScreen";
import SupportScreen from "@/UI/screens/SupportScreen";
// --- Logic --------------------------------------------------------------------------------------
function RootLayout() {
    return (
        <>
            <Outlet />
            <PopupPaper />
        </>
    );
}

const router = createBrowserRouter([
    {
        element: <RootLayout />,
        children: [
            /** The only route outside the shell. There is no organization to pick after signing in. */
            { path: "/login", element: <LoginScreen /> },
            {
                path: "/",
                element: <DashboardLayout />,
                children: [
                    {
                        /** Every section has an API and a screen now, so nothing here is a placeholder. */
                        index: true,
                        element: (
                            <RoleRoute model="home">
                                <HomeScreen />
                            </RoleRoute>
                        ),
                    },
                    /**
                     * One `organizations` segment, two screens. **The segment is a slug, not an id**:
                     * `/organizations/al-rabwah` is a link somebody can read, and it is the same slug
                     * the company's public catalog answers on — so the two addresses agree.
                     */
                    {
                        path: "organizations",
                        element: (
                            <RoleRoute model="organizations">
                                <OrganizationsScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "organizations/:slug",
                        element: (
                            <RoleRoute model="organizations">
                                <OrganizationScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "subscriptions",
                        element: (
                            <RoleRoute model="subscriptions">
                                <SubscriptionsScreen />
                            </RoleRoute>
                        ),
                    },
                    /**
                     * Where the marketing site's form lands, and the only list in this app whose rows
                     * belong to no company — that is what they are for.
                     */
                    {
                        path: "enquiries",
                        element: (
                            <RoleRoute model="enquiries">
                                <EnquiriesScreen />
                            </RoleRoute>
                        ),
                    },
                    /** Every admin, like the rest of the system — only `admins` needs `isSuper`. */
                    {
                        path: "support",
                        element: (
                            <RoleRoute model="support">
                                <SupportScreen />
                            </RoleRoute>
                        ),
                    },
                    /**
                     * One `clients` segment, two screens — and the record one is reached by **id**
                     * rather than by phone number. The phone is the identity, which is exactly why it
                     * is not in the URL: a mistyped number belongs in a search box that answers with a
                     * list, not in an address that answers with nothing.
                     */
                    {
                        path: "clients",
                        element: (
                            <RoleRoute model="clients">
                                <ClientsScreen />
                            </RoleRoute>
                        ),
                    },
                    {
                        path: "clients/:id",
                        element: (
                            <RoleRoute model="clients">
                                <ClientScreen />
                            </RoleRoute>
                        ),
                    },
                    /**
                     * Super-only, and the gate is the same `canAccess` the sidebar reads — `admins` is
                     * the one entry in `ADMIN_SUPER_MODELS`, so a non-super admin never sees the tab
                     * and cannot reach the path either.
                     */
                    {
                        path: "admins",
                        element: (
                            <RoleRoute model="admins">
                                <AdminsScreen />
                            </RoleRoute>
                        ),
                    },
                    /**
                     * The account's own page — every admin has one, so it carries no gate. `/auth/me`
                     * fills it and `/auth/password` is the only thing it can do.
                     */
                    { path: "profile", element: <ProfileScreen /> },
                ],
            },
            { path: "*", element: <NotFoundScreen /> },
        ],
    },
]);

export default function Router() {
    const lang = useLang((state) => state.lang) ?? "ar";

    useEffect(() => {
        document.documentElement.lang = lang;
        document.documentElement.dir = constants.rtlLanguages.includes(lang as "ar") ? "rtl" : "ltr";
    }, [lang]);

    return <RouterProvider router={router} />;
}
